import React, { useState } from 'react';
import { Box, Typography, Grid, Paper, Divider } from '@mui/material';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';

interface Step2SimViewProps {
  data: {
    referenceYear: number;
    hasFamilyFarmersMarket: boolean | null;
    numberOfFamilyFarmers: number;
    annualMarketValue: number;
    totalFamilyFarmingValue: number;
    numberOfPermanentMarkets: number;
    numberOfNonPermanentMarkets: number;
  };
}

const InfoField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box mb={1}>
    <Typography variant="subtitle1" fontWeight={600}>
      {label}
    </Typography>
    <Typography variant="body1">
      {value === null || value === undefined || value === '' ? 'Não informado' : value}
    </Typography>
  </Box>
);

const booleanText = (value: boolean | null) =>
  value === true ? 'Sim' : value === false ? 'Não' : 'Não informado';

const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return 'Não informado';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return 'Não informado';
  return numValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const Step2SimView: React.FC<Step2SimViewProps> = ({ data }) => {
  const [open, setOpen] = useState<boolean>(true);

  return (
    <Paper elevation={1} sx={{ p: 0, mb: 4 }}>
      <Box
        sx={{ px: 3, pt: 2, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(!open)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              color: "#000000DE",
              fontSize: "20px",
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {open ? (
              <KeyboardArrowUpOutlined
                sx={{ color: "text.secondary", fontSize: 20 }}
              />
            ) : (
              <KeyboardArrowDownOutlined
                sx={{ color: "text.secondary", fontSize: 20 }}
              />
            )}
            Feiras da Agricultura Familiar
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
      </Box>

      {open && (
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InfoField
                label="O município possui feira da agricultura familiar?"
                value={booleanText(data.hasFamilyFarmersMarket)}
              />
            </Grid>

            {data.hasFamilyFarmersMarket && (
              <>
                <Grid item xs={12}>
                  <InfoField
                    label="Quantos agricultores(as) comercializam nas feiras?"
                    value={data.numberOfFamilyFarmers || 'Não informado'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InfoField
                    label="Qual o valor comercializado anualmente somente nas feiras?"
                    value={formatCurrency(data.annualMarketValue)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InfoField
                    label="Qual o valor comercializado anualmente pela agricultura familiar?"
                    value={formatCurrency(data.totalFamilyFarmingValue)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                      Quantas feiras existem no município?
                    </Typography>
                    <Box sx={{ ml: 2 }}>
                      <Box mb={1}>
                        <Typography variant="body2" color="textSecondary">
                          Permanentes
                        </Typography>
                        <Typography variant="body1">
                          {data.numberOfPermanentMarkets ?? 'Não informado'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Livres
                        </Typography>
                        <Typography variant="body1">
                          {data.numberOfNonPermanentMarkets ?? 'Não informado'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      )}
    </Paper>
  );
}

export default Step2SimView;
